import React, { useEffect } from "react";

import { QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import { pdfjs } from "react-pdf";
import { Provider } from "react-redux";

import { MarkerWidget } from "./components/MarkerWidget";
import { PersistentToast } from "./components/PersistentToast";
import { Toast } from "./components/Toast";
import { TranscriptionStatusNotifier } from "./components/TranscriptionStatusNotifier";
import { TooltipProvider } from "./components/ui/tooltip";
import { TOKEN_REFRESH_INTERVAL } from "./constants/auth";
import { useThemeSync } from "./context/ThemeContext";
import "./index.css";
import { router } from "./router";
import { store } from "./store";
import { queryClient } from "./utils/reactQueryClient";
import {
  cleanupTokenRefresh,
  setupGlobalAuthErrorHandler,
  setupTokenRefresh,
} from "./utils/tokenManager";

// Resolve the pdfjs worker locally from the installed package (Vite). Must
// match react-pdf's bundled pdfjs (4.8.69) and instantiate reliably, or the
// text layer fails while the canvas still paints — see FilePreview/constants.
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url
).toString();

function App() {
  useThemeSync();

  useEffect(() => {
    // Set up token refresh interval
    setupTokenRefresh(TOKEN_REFRESH_INTERVAL);

    // Set up global authentication error handler
    setupGlobalAuthErrorHandler();

    // Clean up on component unmount
    return () => {
      cleanupTokenRefresh();
    };
  }, []);

  return (
    <Provider store={store}>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <RouterProvider router={router} />

          <MarkerWidget />
          <Toast />
          <PersistentToast />
          <TranscriptionStatusNotifier />
        </TooltipProvider>
      </QueryClientProvider>
    </Provider>
  );
}

export default App;
